import { useState } from 'react';
import { resolveImageUrl } from '@/lib/imageUrl';
import { buildImageSrcSet } from '@/lib/imageOptimizer';

interface ProductImageProps {
  /** URL cruda de la imagen (se resuelve internamente con el relay). */
  url: string | null | undefined;
  /** Nombre del producto, usado como alt y como placeholder. */
  name: string;
  /** Clases para el <img>. */
  className?: string;
  /** Clases para el contenedor del placeholder (fondo y tamaño). */
  placeholderClassName?: string;
  /** Tamaño del icono dentro del placeholder. */
  iconClassName?: string;
  /** Muestra el nombre del producto en el placeholder (útil en imágenes grandes). */
  showName?: boolean;
  /** Atributo sizes para imágenes responsive (solo si hay variantes). */
  sizes?: string;
  /** true para imágenes visibles de inmediato: carga prioritaria, sin lazy. */
  priority?: boolean;
}

/**
 * Imagen de producto con fallback: si la imagen no carga (ni por el relay),
 * muestra un placeholder con el nombre del producto en lugar de un hueco vacío.
 * Sirve variantes responsive (srcset), usa lazy loading por defecto, y aplica
 * protecciones básicas anti-descarga (sin menú contextual ni arrastre).
 */
export default function ProductImage({
  url,
  name,
  className = '',
  placeholderClassName = '',
  iconClassName = 'text-3xl',
  showName = true,
  sizes,
  priority = false,
}: ProductImageProps) {
  const [failed, setFailed] = useState(false);
  const src = resolveImageUrl(url);
  const srcSet = buildImageSrcSet(url);

  if (!src || failed) {
    return (
      <div
        className={`flex flex-col items-center justify-center gap-2 bg-background-100 text-foreground-400 p-3 ${placeholderClassName}`}
      >
        <i className={`ri-image-line ${iconClassName}`} />
        {showName && (
          <span className="text-xs font-medium text-foreground-500 text-center leading-snug line-clamp-2 break-words">
            {name || 'Sin imagen'}
          </span>
        )}
      </div>
    );
  }

  return (
    <img
      src={src}
      srcSet={srcSet}
      sizes={srcSet ? sizes ?? '100vw' : undefined}
      alt={name}
      title={name}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      fetchPriority={priority ? 'high' : 'auto'}
      draggable={false}
      onContextMenu={(e) => e.preventDefault()}
      onError={() => setFailed(true)}
      className={`select-none ${className}`}
    />
  );
}